import { useState, useEffect, useMemo } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
// Shared modal chrome, first built for the attendance admin screens.
import ModalShell from '../../Attendance/Component/ModalShell';
import { createShift, updateShift } from '../../../../redux/actions/SocietyPortal/ShiftAction/ShiftAction';
import {
    BREAK_TYPES,
    toBool,
    spanMinutes,
    formatMinutes,
    formatWallTime,
} from '../../../../consts/shiftConstants';

// Create / edit a shift definition — POST /api/shifts, PATCH /api/shifts/:id.
//
// Duration is DERIVED, never sent: the API computes span_minutes and
// scheduled_minutes from the times and the break. The preview below mirrors
// that maths so a transposed start/end is obvious before saving.

const EMPTY = {
    shift_code: '',
    shift_name: '',
    description: '',
    start_time: '09:00',
    end_time: '18:00',
    break_minutes: '0',
    break_type: 'UNPAID',
    useSocietyDayThresholds: true,
    full_day_minutes: '',
    half_day_minutes: '',
    late_grace_minutes: '0',
    early_exit_grace_minutes: '0',
    useDefaultOvertime: true,
    overtime_after_minutes: '',
    punch_in_window_before_minutes: '60',
    punch_out_window_after_minutes: '120',
    color_hex: '#1E88E5',
    display_order: '0',
    is_default: false,
    is_active: true,
};

const fromShift = (shift) => ({
    shift_code: shift.shift_code || '',
    shift_name: shift.shift_name || '',
    description: shift.description || '',
    start_time: formatWallTime(shift.start_time),
    end_time: formatWallTime(shift.end_time),
    break_minutes: String(shift.break_minutes ?? 0),
    break_type: shift.break_type || 'UNPAID',
    useSocietyDayThresholds: shift.full_day_minutes == null && shift.half_day_minutes == null,
    full_day_minutes: shift.full_day_minutes == null ? '' : String(shift.full_day_minutes),
    half_day_minutes: shift.half_day_minutes == null ? '' : String(shift.half_day_minutes),
    late_grace_minutes: String(shift.late_grace_minutes ?? 0),
    early_exit_grace_minutes: String(shift.early_exit_grace_minutes ?? 0),
    useDefaultOvertime: shift.overtime_after_minutes == null,
    overtime_after_minutes: shift.overtime_after_minutes == null ? '' : String(shift.overtime_after_minutes),
    punch_in_window_before_minutes: String(shift.punch_in_window_before_minutes ?? 60),
    punch_out_window_after_minutes: String(shift.punch_out_window_after_minutes ?? 120),
    color_hex: shift.color_hex || '#1E88E5',
    display_order: String(shift.display_order ?? 0),
    is_default: toBool(shift.is_default),
    is_active: toBool(shift.is_active),
});

const num = (v) => (v === '' || v == null ? 0 : Number(v));

// Module-level, not nested in the component: a sub-component redeclared on
// every render is a new element type each time, so React would remount the
// input underneath it and the field would lose focus on every keystroke.
const Field = ({ label, error, hint, children }) => (
    <div>
        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">{label}</label>
        {children}
        {error
            ? <p className="tw-text-[11px] tw-text-red-500 tw-mt-1 tw-mb-0">{error}</p>
            : hint ? <p className="tw-text-[10px] tw-text-gray-400 tw-mt-1 tw-mb-0">{hint}</p> : null}
    </div>
);

const ShiftFormModal = ({ isOpen, shift, onClose, onDone, onCodeTaken }) => {
    const dispatch = useDispatch();
    const { shiftMutationLoading } = useSelector(state => state.ShiftReducer || {});
    const isEdit = Boolean(shift?.shift_id);

    const [form, setForm] = useState(EMPTY);
    const [errors, setErrors] = useState({});
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        if (!isOpen) return;
        setForm(shift?.shift_id ? fromShift(shift) : EMPTY);
        setErrors({});
        setShowAdvanced(false);
    }, [isOpen, shift]);

    const set = (field, value) => {
        setForm(prev => ({ ...prev, [field]: value }));
        setErrors(prev => ({ ...prev, [field]: undefined }));
    };

    // Live preview of what the API will compute and store.
    const preview = useMemo(() => {
        const span = spanMinutes(form.start_time, form.end_time);
        if (span == null) return null;
        const brk = form.break_type === 'UNPAID' ? num(form.break_minutes) : 0;
        return {
            span,
            // A PAID break is informational — it is NOT deducted.
            scheduled: Math.max(span - brk, 0),
            crossesMidnight: (form.end_time || '') <= (form.start_time || ''),
        };
    }, [form.start_time, form.end_time, form.break_minutes, form.break_type]);

    const validate = () => {
        const e = {};
        if (!form.shift_code.trim()) e.shift_code = 'Required';
        else if (form.shift_code.trim().length > 20) e.shift_code = 'Max 20 characters';
        if (!form.shift_name.trim()) e.shift_name = 'Required';
        else if (form.shift_name.trim().length > 100) e.shift_name = 'Max 100 characters';
        if (!form.start_time) e.start_time = 'Required';
        if (!form.end_time) e.end_time = 'Required';

        const brk = num(form.break_minutes);
        if (brk < 0 || brk > 1440) e.break_minutes = 'Between 0 and 1440';
        else if (preview && brk >= preview.span) {
            e.break_minutes = `Must be less than the ${formatMinutes(preview.span)} span`;
        }

        if (!form.useSocietyDayThresholds) {
            const full = num(form.full_day_minutes);
            const half = num(form.half_day_minutes);
            if (form.full_day_minutes === '') e.full_day_minutes = 'Required, or use society defaults';
            else if (full < 0 || full > 1440) e.full_day_minutes = 'Between 0 and 1440';
            if (form.half_day_minutes !== '' && half > full) {
                e.half_day_minutes = 'Cannot exceed the full-day minutes';
            }
        }
        if (!form.useDefaultOvertime) {
            const ot = num(form.overtime_after_minutes);
            if (form.overtime_after_minutes === '') e.overtime_after_minutes = 'Required, or use the scheduled hours';
            else if (ot < 0 || ot > 1440) e.overtime_after_minutes = 'Between 0 and 1440';
        }
        if (form.color_hex && !/^#[0-9a-fA-F]{6}$/.test(form.color_hex)) {
            e.color_hex = 'Must be #RRGGBB';
        }
        return e;
    };

    // The API's own field set, with the form's "use defaults" toggles resolved
    // back to the nulls that mean "inherit".
    const toApiValues = () => ({
        shift_code: form.shift_code.trim().toUpperCase(),
        shift_name: form.shift_name.trim(),
        description: form.description.trim() || null,
        start_time: form.start_time,
        end_time: form.end_time,
        break_minutes: num(form.break_minutes),
        break_type: form.break_type,
        full_day_minutes: form.useSocietyDayThresholds ? null : num(form.full_day_minutes),
        // null half-day means "half of the full day", which is the sane default
        // even when a custom full day was set.
        half_day_minutes: form.useSocietyDayThresholds || form.half_day_minutes === ''
            ? null
            : num(form.half_day_minutes),
        late_grace_minutes: num(form.late_grace_minutes),
        early_exit_grace_minutes: num(form.early_exit_grace_minutes),
        overtime_after_minutes: form.useDefaultOvertime ? null : num(form.overtime_after_minutes),
        punch_in_window_before_minutes: num(form.punch_in_window_before_minutes),
        punch_out_window_after_minutes: num(form.punch_out_window_after_minutes),
        color_hex: form.color_hex || null,
        display_order: num(form.display_order),
        is_default: form.is_default,
        is_active: form.is_active,
    });

    const handleSubmit = () => {
        const e = validate();
        if (Object.keys(e).length > 0) {
            setErrors(e);
            return;
        }

        const values = toApiValues();
        const onError = (info) => {
            // The 409 carries the shift that already owns the code, so the list
            // can open it instead of just reporting a clash.
            if (info.code === 'SHIFT_CODE_TAKEN') {
                setErrors({ shift_code: 'This code is already used by another shift' });
                onCodeTaken?.(info.data?.shift_id, values.shift_code);
            }
        };

        if (!isEdit) {
            dispatch(createShift({ ...values, onSuccess: (data) => { onClose(); onDone?.(data); }, onError }));
            return;
        }

        // PATCH has no defaults — send only what changed, so an untouched field
        // is never silently rewritten.
        const before = shiftApiValues(shift);
        const changed = {};
        Object.entries(values).forEach(([key, value]) => {
            if (before[key] !== value) changed[key] = value;
        });
        if (Object.keys(changed).length === 0) {
            onClose();
            return;
        }
        dispatch(updateShift({ shift_id: shift.shift_id, ...changed, onSuccess: (data) => { onClose(); onDone?.(data); }, onError }));
    };

    const inputClass = (field) =>
        `tw-w-full tw-border tw-rounded-md tw-px-3 tw-py-2 tw-text-sm focus:tw-outline-none focus:tw-ring-1 ${errors[field] ? 'tw-border-red-300 focus:tw-ring-red-400' : 'tw-border-gray-200 focus:tw-ring-blue-500'}`;

    return (
        <ModalShell
            isOpen={isOpen}
            title={isEdit ? `Edit ${shift?.shift_name || 'shift'}` : 'New shift'}
            icon="hugeicons:time-schedule"
            onClose={onClose}
            busy={shiftMutationLoading}
            maxWidth="tw-max-w-2xl"
            actions={
                <button
                    type="button"
                    onClick={handleSubmit}
                    disabled={shiftMutationLoading}
                    className="tw-px-3 tw-py-1.5 tw-rounded-md tw-bg-blue-600 tw-text-white tw-text-xs tw-font-medium hover:tw-bg-blue-700 disabled:tw-opacity-50"
                >
                    {shiftMutationLoading ? 'Saving…' : isEdit ? 'Save changes' : 'Create shift'}
                </button>
            }
        >
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3">
                <Field label="Shift code *" error={errors.shift_code} hint="Stored in upper case, unique per society">
                    <input
                        type="text"
                        value={form.shift_code}
                        maxLength={20}
                        // Uppercased as you type, because the API uppercases it
                        // anyway — otherwise you type "night" and NIGHT comes back.
                        onChange={(e) => set('shift_code', e.target.value.toUpperCase())}
                        placeholder="NIGHT"
                        className={inputClass('shift_code')}
                    />
                </Field>
                <Field label="Shift name *" error={errors.shift_name}>
                    <input
                        type="text"
                        value={form.shift_name}
                        maxLength={100}
                        onChange={(e) => set('shift_name', e.target.value)}
                        placeholder="Night Shift"
                        className={inputClass('shift_name')}
                    />
                </Field>

                <Field label="Starts at *" error={errors.start_time}>
                    <input type="time" value={form.start_time} onChange={(e) => set('start_time', e.target.value)} className={inputClass('start_time')} />
                </Field>
                <Field label="Ends at *" error={errors.end_time} hint="Earlier than the start means the shift crosses midnight">
                    <input type="time" value={form.end_time} onChange={(e) => set('end_time', e.target.value)} className={inputClass('end_time')} />
                </Field>
            </div>

            {/* Derived values, exactly as the API will compute them */}
            {preview && (
                <div className="tw-mt-3 tw-flex tw-items-center tw-gap-3 tw-flex-wrap tw-rounded-md tw-bg-slate-50 tw-border tw-border-slate-100 tw-px-3 tw-py-2">
                    <span className="tw-text-[11px] tw-text-slate-500">
                        <strong className="tw-text-slate-700">{formatMinutes(preview.span)}</strong> span
                    </span>
                    <span className="tw-text-[11px] tw-text-slate-500">
                        <strong className="tw-text-slate-700">{formatMinutes(preview.scheduled)}</strong> working
                    </span>
                    {preview.crossesMidnight && (
                        <span className="tw-inline-flex tw-items-center tw-gap-1 tw-text-[11px] tw-text-indigo-600">
                            <Icon icon="mdi:weather-night" /> crosses midnight
                        </span>
                    )}
                    {form.start_time === form.end_time && (
                        <span className="tw-text-[11px] tw-text-slate-400">24-hour shift</span>
                    )}
                </div>
            )}

            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mt-3">
                <Field label="Break (minutes)" error={errors.break_minutes}>
                    <input type="number" min={0} max={1440} value={form.break_minutes} onChange={(e) => set('break_minutes', e.target.value)} className={inputClass('break_minutes')} />
                </Field>
                <Field
                    label="Break type"
                    error={errors.break_type}
                    hint={form.break_type === 'PAID' ? 'Paid breaks are informational — they are not deducted' : 'Unpaid breaks are deducted from the working hours'}
                >
                    <select value={form.break_type} onChange={(e) => set('break_type', e.target.value)} className={inputClass('break_type')}>
                        {BREAK_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
                    </select>
                </Field>
            </div>

            {/* Attendance is not wired to shifts yet, so nothing on the server
                reads any of these. They are stored faithfully and will start
                mattering when it is — but showing an admin a grace-period field
                that changes nothing today is worse than not offering it, so
                they stay collapsed. */}
            <div className="tw-mt-4 tw-rounded-md tw-border tw-border-gray-100">
                <button
                    type="button"
                    onClick={() => setShowAdvanced(v => !v)}
                    className="tw-w-full tw-flex tw-items-center tw-justify-between tw-px-3 tw-py-2 tw-text-xs tw-font-medium tw-text-gray-600 hover:tw-bg-gray-50"
                >
                    <span className="tw-flex tw-items-center tw-gap-1.5">
                        <Icon icon={showAdvanced ? 'mdi:chevron-down' : 'mdi:chevron-right'} />
                        Advanced — attendance thresholds &amp; grace
                    </span>
                    <span className="tw-text-[10px] tw-text-gray-400 tw-font-normal">not used yet</span>
                </button>
                {showAdvanced && (
                    <div className="tw-px-3 tw-pb-3">
                        <p className="tw-text-[11px] tw-text-gray-500 tw-bg-slate-50 tw-rounded-md tw-px-3 tw-py-2 tw-mb-0">
                            Attendance does not read shifts yet, so none of these change anything today. They are saved and will take effect when it does.
                        </p>
                {/* Grace — the single most misunderstood setting on this form */}
                <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mt-3">
                    <Field
                        label="Late grace (minutes)"
                        error={errors.late_grace_minutes}
                        hint={num(form.late_grace_minutes) > 0
                            ? `Arrive within ${form.late_grace_minutes} min and it counts as 0 late. Arrive a minute later and the FULL lateness is counted from the scheduled start — grace is a cliff, not a discount.`
                            : 'Any lateness is counted in full'}
                    >
                        <input type="number" min={0} max={1440} value={form.late_grace_minutes} onChange={(e) => set('late_grace_minutes', e.target.value)} className={inputClass('late_grace_minutes')} />
                    </Field>
                    <Field label="Early-exit grace (minutes)" error={errors.early_exit_grace_minutes} hint="Same cliff behaviour at the end of the shift">
                        <input type="number" min={0} max={1440} value={form.early_exit_grace_minutes} onChange={(e) => set('early_exit_grace_minutes', e.target.value)} className={inputClass('early_exit_grace_minutes')} />
                    </Field>
                </div>

                {/* Day thresholds */}
                <div className="tw-mt-4 tw-rounded-md tw-border tw-border-gray-100 tw-p-3">
                    <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.useSocietyDayThresholds}
                            onChange={(e) => set('useSocietyDayThresholds', e.target.checked)}
                        />
                        Use the society&apos;s default full/half-day thresholds
                    </label>
                    {!form.useSocietyDayThresholds && (
                        <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mt-3">
                            <Field label="Full day (minutes)" error={errors.full_day_minutes} hint="A 4-hour part-time shift with 240 here earns a full PRESENT day">
                                <input type="number" min={0} max={1440} value={form.full_day_minutes} onChange={(e) => set('full_day_minutes', e.target.value)} className={inputClass('full_day_minutes')} />
                            </Field>
                            <Field label="Half day (minutes)" error={errors.half_day_minutes} hint="Leave blank for half of the full day">
                                <input type="number" min={0} max={1440} value={form.half_day_minutes} onChange={(e) => set('half_day_minutes', e.target.value)} className={inputClass('half_day_minutes')} />
                            </Field>
                        </div>
                    )}
                </div>

                {/* Overtime + punch windows */}
                <div className="tw-mt-3 tw-rounded-md tw-border tw-border-gray-100 tw-p-3">
                    <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                        <input
                            type="checkbox"
                            checked={form.useDefaultOvertime}
                            onChange={(e) => set('useDefaultOvertime', e.target.checked)}
                        />
                        Overtime starts once the scheduled hours are done
                    </label>
                    {!form.useDefaultOvertime && (
                        <div className="tw-mt-3 sm:tw-w-1/2">
                            <Field label="Overtime after (minutes)" error={errors.overtime_after_minutes}>
                                <input type="number" min={0} max={1440} value={form.overtime_after_minutes} onChange={(e) => set('overtime_after_minutes', e.target.value)} className={inputClass('overtime_after_minutes')} />
                            </Field>
                        </div>
                    )}
                    <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mt-3">
                        <Field label="Punch-in window before start (min)" error={errors.punch_in_window_before_minutes} hint="How early a punch still counts for this shift">
                            <input type="number" min={0} max={1440} value={form.punch_in_window_before_minutes} onChange={(e) => set('punch_in_window_before_minutes', e.target.value)} className={inputClass('punch_in_window_before_minutes')} />
                        </Field>
                        <Field label="Punch-out window after end (min)" error={errors.punch_out_window_after_minutes}>
                            <input type="number" min={0} max={1440} value={form.punch_out_window_after_minutes} onChange={(e) => set('punch_out_window_after_minutes', e.target.value)} className={inputClass('punch_out_window_after_minutes')} />
                        </Field>
                    </div>
                </div>
                    </div>
                )}
            </div>

            {/* Presentation */}
            <div className="tw-grid tw-grid-cols-1 sm:tw-grid-cols-2 tw-gap-3 tw-mt-3">
                <Field label="Colour" error={errors.color_hex} hint="Used for this shift's chip on the employee and assignment lists">
                    <div className="tw-flex tw-items-center tw-gap-2">
                        <input
                            type="color"
                            value={/^#[0-9a-fA-F]{6}$/.test(form.color_hex) ? form.color_hex : '#1E88E5'}
                            onChange={(e) => set('color_hex', e.target.value.toUpperCase())}
                            className="tw-w-10 tw-h-9 tw-rounded tw-border tw-border-gray-200 tw-cursor-pointer tw-p-0.5"
                        />
                        <input
                            type="text"
                            value={form.color_hex}
                            onChange={(e) => set('color_hex', e.target.value.toUpperCase())}
                            placeholder="#1E88E5"
                            className={inputClass('color_hex')}
                        />
                    </div>
                </Field>
                <Field label="Display order" error={errors.display_order} hint="Lower numbers sort first in the shift list">
                    <input type="number" min={0} max={65535} value={form.display_order} onChange={(e) => set('display_order', e.target.value)} className={inputClass('display_order')} />
                </Field>
            </div>

            <Field label="Description" error={errors.description}>
                <textarea
                    rows={2}
                    value={form.description}
                    maxLength={2000}
                    onChange={(e) => set('description', e.target.value)}
                    className={`${inputClass('description')} tw-mt-3`}
                />
            </Field>

            <div className="tw-flex tw-items-center tw-gap-5 tw-mt-3 tw-flex-wrap">
                <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                    <input type="checkbox" checked={form.is_active} onChange={(e) => set('is_active', e.target.checked)} />
                    Active
                </label>
                <label className="tw-flex tw-items-center tw-gap-2 tw-text-xs tw-text-gray-700 tw-cursor-pointer">
                    <input type="checkbox" checked={form.is_default} onChange={(e) => set('is_default', e.target.checked)} />
                    Default shift
                    <span className="tw-text-[10px] tw-text-gray-400">(clears the current default)</span>
                </label>
            </div>

            {isEdit && (
                <p className="tw-text-[11px] tw-text-amber-700 tw-bg-amber-50 tw-rounded-md tw-px-3 tw-py-2 tw-mt-3 tw-mb-0">
                    <Icon icon="mdi:information-outline" className="tw-inline tw-mr-1" />
                    Assignments point at this shift rather than copying its timings, so a change here takes effect immediately for everyone assigned to it.
                </p>
            )}
        </ModalShell>
    );
};

// The stored row, reduced to the same shape toApiValues() produces, so a PATCH
// can be built from a plain value-by-value diff.
const shiftApiValues = (shift) => ({
    shift_code: shift.shift_code || '',
    shift_name: shift.shift_name || '',
    description: shift.description || null,
    start_time: formatWallTime(shift.start_time),
    end_time: formatWallTime(shift.end_time),
    break_minutes: shift.break_minutes ?? 0,
    break_type: shift.break_type || 'UNPAID',
    full_day_minutes: shift.full_day_minutes ?? null,
    half_day_minutes: shift.half_day_minutes ?? null,
    late_grace_minutes: shift.late_grace_minutes ?? 0,
    early_exit_grace_minutes: shift.early_exit_grace_minutes ?? 0,
    overtime_after_minutes: shift.overtime_after_minutes ?? null,
    punch_in_window_before_minutes: shift.punch_in_window_before_minutes ?? 60,
    punch_out_window_after_minutes: shift.punch_out_window_after_minutes ?? 120,
    color_hex: shift.color_hex || null,
    display_order: shift.display_order ?? 0,
    is_default: toBool(shift.is_default),
    is_active: toBool(shift.is_active),
});

export default ShiftFormModal;
